import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionNav from '../../../components/layout/SectionNav';
import { researchTabs } from '../../../nav/publicNav';
import { Loader2, BarChart2, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

const BEAT_COLORS = { beat: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', miss: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', 'in-line': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' };
const BEAT_ICONS = { beat: TrendingUp, miss: TrendingDown, 'in-line': Minus };

export default function EarningsAnalysisPage() {
  const navigate = useNavigate();
  const [earnings, setEarnings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIsin, setSelectedIsin] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const fetchEarnings = useCallback(async (pg) => {
    setLoading(true);
    setError(null);
    const p = pg || page;
    try {
      const res = await fetch(`${API}/earnings/recent?page=${p}&page_size=20`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEarnings(data.items || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [page]);

  const fetchAnalysis = async (isin) => {
    setSelectedIsin(isin);
    setAnalysisLoading(true);
    setAnalysis(null);
    try {
      const res = await fetch(`${API}/earnings/analysis/${isin}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAnalysis(data);
    } catch (e) { setError(e.message); }
    setAnalysisLoading(false);
  };

  useEffect(() => { fetchEarnings(); }, []);

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-4">
      <SectionNav tabs={researchTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-emerald-500" /> Earnings Analysis
        </h1>
        <p className="text-xs text-muted-foreground">AI-powered earnings call analysis with beat/miss classification</p>
      </div>

      {analysis && (
        <Card className="border-emerald-500/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">{analysis.company_name} ({analysis.symbol})</CardTitle>
                <p className="text-xs text-muted-foreground">{analysis.quarter} FY{analysis.fiscal_year} · {analysis.call_date}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setAnalysis(null); setSelectedIsin(null); }}>
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {analysis.ratios && (
              <div className="flex flex-wrap gap-3 text-xs">
                {analysis.ratios.pe && <Badge variant="secondary">PE: {analysis.ratios.pe.toFixed(1)}</Badge>}
                {analysis.ratios.roe && <Badge variant="secondary">ROE: {analysis.ratios.roe.toFixed(1)}%</Badge>}
                {analysis.ratios.opm && <Badge variant="secondary">OPM: {analysis.ratios.opm.toFixed(1)}%</Badge>}
                {analysis.ratios.npm && <Badge variant="secondary">NPM: {analysis.ratios.npm.toFixed(1)}%</Badge>}
              </div>
            )}
            {analysis.shareholding?.length > 0 && (
              <div className="flex flex-wrap gap-2 text-[10px]">
                {analysis.shareholding.map((s, i) => (
                  <span key={i} className="text-muted-foreground">
                    {s.quarter}: FII {s.fii?.toFixed(1)}% · DII {s.dii?.toFixed(1)}%
                  </span>
                ))}
              </div>
            )}
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-semibold">AI Analysis</span>
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{analysis.analysis}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {earnings.map((e, i) => {
              const Icon = BEAT_ICONS[e.beat_miss] || Minus;
              return (
                <Card key={i} className={`cursor-pointer transition-all hover:shadow-md ${selectedIsin === e.isin ? 'ring-2 ring-emerald-500' : ''}`}
                  onClick={() => fetchAnalysis(e.isin)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-sm">{e.company_name}</p>
                        <p className="text-xs text-muted-foreground">{e.symbol} · {e.quarter} FY{e.fiscal_year}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${BEAT_COLORS[e.beat_miss]}`}>
                        <Icon className="w-3 h-3" />
                        {e.beat_miss?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex gap-3 text-[10px] text-muted-foreground">
                      {e.opm != null && <span>OPM: {e.opm.toFixed(1)}%</span>}
                      {e.pe != null && <span>PE: {e.pe.toFixed(1)}</span>}
                      {e.call_date && <span>{e.call_date}</span>}
                    </div>
                    {selectedIsin === e.isin && analysisLoading && (
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Loader2 className="w-3 h-3 animate-spin" /> Analyzing with AI...
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchEarnings(page - 1)}>
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => fetchEarnings(page + 1)}>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
